export const PWG_LINE_TYPES = ["regular", "goblin", "demon"];
export const PRIZEPICKS_LINE_TYPES = PWG_LINE_TYPES;

// Existing sport feeds expose the same provider concept under several names.
// These are explicit source mappings only; projection-line numbers are never
// consulted when determining line type.
export const LINE_TYPE_SOURCE_FIELDS = Object.freeze([
  "Line Type",
  "line_type",
  "lineType",
  "Source Line Type",
  "source_line_type",
  "PrizePicks Odds Type",
  "prizepicks_odds_type",
  "Odds Type",
  "odds_type",
]);

const TYPE_ALIASES = Object.freeze({
  regular: "regular",
  primary: "regular",
  standard: "regular",
  normal: "regular",
  unknown: "unknown",
  promo: "promo",
  goblin: "goblin",
  demon: "demon",
});

function normalizedSourceValue(value) {
  return String(value ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

export function canonicalLineType(rowOrValue) {
  if (typeof rowOrValue === "string") {
    const value = normalizedSourceValue(rowOrValue);
    return value ? (TYPE_ALIASES[value] || "unknown") : "regular";
  }
  for (const field of LINE_TYPE_SOURCE_FIELDS) {
    const value = normalizedSourceValue(rowOrValue?.[field]);
    if (value) return TYPE_ALIASES[value] || "unknown";
  }
  return "regular";
}

export function supportedLineTypes(rows) {
  const present = new Set((Array.isArray(rows) ? rows : []).map(canonicalLineType));
  return PWG_LINE_TYPES.filter((type) => present.has(type));
}

export function defaultLineTypesForSport() {
  return ["regular"];
}

export function lineTypesAfterSportChange() {
  return ["regular"];
}

export function customerLineTypeLabel(rowOrType) {
  return canonicalLineType(rowOrType).toUpperCase();
}

export function projectionIdentity(row, fallback = "") {
  const projectionId = String(row?.["Projection ID"] ?? row?.ProjectionID ?? row?.projection_id ?? "").trim();
  if (projectionId) return `projection:${projectionId}`;
  return fallback;
}

export function filterRowsByLineTypes(rows, selectedTypes) {
  const selected = new Set((Array.isArray(selectedTypes) ? selectedTypes : []).map((type) => (
    String(type || "").trim().toLowerCase() === "all" ? "all" : canonicalLineType(String(type || ""))
  )));
  // Empty state is fail-safe Regular, never an implicit All selection.
  if (selected.size === 0) selected.add("regular");
  return (Array.isArray(rows) ? rows : []).filter((row) => (
    selected.has("all") || selected.has(canonicalLineType(row))
  ));
}

export function propCardAccessibleLabel(row, propLabel = "") {
  const player = String(row?.Player || row?.Name || "Player").trim();
  const market = String(propLabel || row?.["Prop Type"] || row?.PropType || "Prop").trim();
  const line = String(row?.["Prop Value"] ?? row?.PropValue ?? "Unavailable").trim();
  return `View details for ${player}, ${market}, ${line}, ${customerLineTypeLabel(row)}`;
}

export function goblinAttainability(row) {
  if (canonicalLineType(row) !== "goblin") return null;
  const score = Number(row?.["Goblin Attainability"]);
  const classification = String(row?.["Goblin Attainability Class"] || "").trim();
  if (!Number.isFinite(score) && !classification) return null;
  return {
    score: Number.isFinite(score) ? score : null,
    classification,
    evidence: String(row?.["Goblin Attainability Evidence"] || ""),
    method: String(row?.["Goblin Attainability Method"] || ""),
  };
}

export function lineTypeTone(type) {
  return {
    regular: { background: "#EFF6FF", border: "#60A5FA", color: "#1D4ED8" },
    goblin: { background: "#ECFDF5", border: "#34D399", color: "#047857" },
    demon: { background: "#FEF2F2", border: "#F87171", color: "#B91C1C" },
  }[canonicalLineType(type)] || { background: "#F8FAFC", border: "#CBD5E1", color: "#475569" };
}
